(function () {
  'use strict';

  const COOKIE_NAME = 'aquatix_visit_count';
  const COOKIE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  const MAX_STORED_COUNT = 6;

  // Message 3 (index 2) is visit-specific copy and is permanently excluded from the
  // random pool. Visits 4+ pick randomly from messages 1, 2, 4, 5, 6 only.
  const RANDOM_POOL = [0, 1, 3, 4, 5];

  // ── Cookie helpers ──────────────────────────────────────────────────────────

  function readCookie() {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)')
    );
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return null;
    }
  }

  function writeCookie(data) {
    const expires = new Date(Date.now() + COOKIE_TTL_MS).toUTCString();
    document.cookie =
      COOKIE_NAME +
      '=' +
      encodeURIComponent(JSON.stringify(data)) +
      '; expires=' +
      expires +
      '; path=/; SameSite=Lax';
  }

  /**
   * Reads current visit count, bumps it (capped at MAX_STORED_COUNT),
   * writes the updated cookie, and returns the NEW count (1-indexed).
   */
  function bumpVisitCount() {
    const saved = readCookie();
    let count = 1;

    if (saved && saved.ts && saved.count) {
      const age = Date.now() - saved.ts;
      if (age < COOKIE_TTL_MS) {
        count = Math.min(saved.count + 1, MAX_STORED_COUNT);
      }
      // Older than 7 days → reset to 1 (default above)
    }

    writeCookie({ count, ts: Date.now() });
    return count;
  }

  // ── Greeting selection ──────────────────────────────────────────────────────

  /**
   * Visit 1 → message 1 (index 0)
   * Visit 2 → message 2 (index 1)
   * Visit 3 → message 3 (index 2) — shown exactly once, never randomly
   * Visit 4+ → random from RANDOM_POOL (indices 0,1,3,4,5; message 3 excluded)
   */
  function pickGreetingIndex(visitCount) {
    if (visitCount === 1) return 0;
    if (visitCount === 2) return 1;
    if (visitCount === 3) return 2;
    return RANDOM_POOL[Math.floor(Math.random() * RANDOM_POOL.length)];
  }

  // ── Timing utilities ────────────────────────────────────────────────────────

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /** Uniformly random float in [min, max). */
  function randRange(min, max) {
    return min + Math.random() * (max - min);
  }

  /** Uniformly random integer in [min, max] inclusive. */
  function randInt(min, max) {
    return Math.floor(min + Math.random() * (max - min + 1));
  }

  /**
   * Returns the punctuation pause duration (ms) for a single character.
   * Ellipsis (...) is handled separately in the typing loop.
   * Returns 0 for non-punctuation characters.
   */
  function getPunctPause(char) {
    switch (char) {
      case '.':        return randRange(600, 900);
      case ',':        return randRange(300, 500);
      case '!':        return randRange(500, 700);
      case '?':        return randRange(500, 700);
      case '\u2014':   return randRange(400, 600); // em dash —
      default:         return 0;
    }
  }

  // ── Typewriter ──────────────────────────────────────────────────────────────

  /**
   * @param {HTMLElement} el           - The greeting <p> element.
   * @param {string}      text         - Full string to type.
   * @param {string}      speed        - 'slow' | 'medium' | 'fast'
   * @param {string}      style        - 'cursor' | 'soft-cursor' | 'no-cursor' | 'terminal'
   * @param {boolean}     useMonospace - Apply monospace font (terminal style only).
   */
  async function typewrite(el, text, speed, style, useMonospace) {
    const BASE = { slow: 110, medium: 55, fast: 22 };
    const base = BASE[speed] || BASE.medium;

    // For terminal style we inject child spans so textContent assignments
    // on the text span don't wipe the cursor element sibling.
    let textNode = el;
    let cursorEl = null;

    if (style === 'terminal') {
      el.innerHTML = '';
      if (useMonospace) el.classList.add('aquatix-hero-video__greeting--monospace');
      const textSpan = document.createElement('span');
      cursorEl = document.createElement('span');
      cursorEl.setAttribute('aria-hidden', 'true');
      cursorEl.className = 'aquatix-typewriter__terminal-cursor';
      cursorEl.textContent = '\u2588'; // █ full-block character
      el.appendChild(textSpan);
      el.appendChild(cursorEl);
      textNode = textSpan;
    } else {
      el.textContent = '';
      if (style === 'cursor') {
        el.classList.add('aquatix-typewriter--cursor');
      } else if (style === 'soft-cursor') {
        el.classList.add('aquatix-typewriter--soft-cursor');
      }
    }

    let i = 0;
    let burstRemaining = 0;               // chars left in post-punctuation speed burst
    let hesitationCounter = randInt(8, 12); // chars until next micro-hesitation

    while (i < text.length) {

      // ── Ellipsis: three consecutive periods typed as a unit ─────────────────
      // Handled before individual characters so the three periods don't each
      // trigger a solo-period pause.
      if (
        text[i] === '.' &&
        i + 2 < text.length &&
        text[i + 1] === '.' &&
        text[i + 2] === '.'
      ) {
        textNode.textContent = text.slice(0, i + 1);
        await sleep(base + randRange(20, 60));
        textNode.textContent = text.slice(0, i + 2);
        await sleep(base + randRange(20, 60));
        textNode.textContent = text.slice(0, i + 3);
        // Dramatic pause after the third dot — the money moment
        await sleep(randRange(800, 1200));
        burstRemaining = randInt(3, 5);
        i += 3;
        continue;
      }

      // ── Normal character ─────────────────────────────────────────────────────
      const inBurst = burstRemaining > 0;

      textNode.textContent = text.slice(0, i + 1);

      // During a post-punctuation burst the typist accelerates as they regain rhythm
      // Jitter is a 0.7–1.3× multiplier so variation scales with the chosen speed.
      const jitter = randRange(0.7, 1.3);
      const charDelay = inBurst
        ? base * 0.5 * jitter
        : base * jitter;

      if (inBurst) burstRemaining--;

      // Check for punctuation — applied as an additional hold after the char delay
      const punctPause = getPunctPause(text[i]);
      if (punctPause > 0) burstRemaining = randInt(3, 5);

      // Micro-hesitation: 1 in every 8–12 characters, skip during burst or punctuation
      let hesitationDelay = 0;
      hesitationCounter--;
      if (hesitationCounter <= 0) {
        hesitationCounter = randInt(8, 12);
        if (!inBurst && punctPause === 0) {
          hesitationDelay = randRange(80, 180);
        }
      }

      await sleep(charDelay + hesitationDelay);
      if (punctPause > 0) await sleep(punctPause);

      i++;
    }

    // ── Post-typing cursor cleanup ────────────────────────────────────────────
    if (style === 'terminal') {
      // 3 more blinks at 500ms each, then the cursor disappears
      await sleep(1500);
      if (cursorEl && cursorEl.parentNode) cursorEl.remove();
    } else if (style !== 'no-cursor') {
      await sleep(1500);
      el.classList.remove(
        'aquatix-typewriter--cursor',
        'aquatix-typewriter--soft-cursor'
      );
    }
  }

  // ── CTA rendering ───────────────────────────────────────────────────────────

  function sanitiseUrl(url) {
    if (!url) return '#';
    return /^(https?:\/\/|\/)/.test(url) ? url : '#';
  }

  function buildCtaElement(greeting) {
    const a = document.createElement('a');
    a.href = sanitiseUrl(greeting.cta_link);

    if (greeting.cta_style === 'button') {
      a.className = 'aquatix-hero-video__cta-btn';
      a.textContent = greeting.cta_text || '';
    } else {
      a.className = 'aquatix-hero-video__cta-link';
      a.textContent = (greeting.cta_text || '') + '\u00a0';
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      a.appendChild(arrow);
    }

    return a;
  }

  function revealCta(ctaWrap, greeting) {
    ctaWrap.innerHTML = '';
    if (!greeting.cta_text || !greeting.cta_link) return;

    ctaWrap.appendChild(buildCtaElement(greeting));
    ctaWrap.removeAttribute('aria-hidden');

    void ctaWrap.offsetWidth; // force reflow so CSS transition fires from opacity:0
    ctaWrap.classList.add('aquatix-hero-video__cta-wrap--visible');
  }

  // ── Section controller ──────────────────────────────────────────────────────

  function AquatixHeroVideoSection(el) {
    this.el = el;
    this.animationStyle = el.dataset.animationStyle || 'cursor';
    this.typingSpeed = el.dataset.typingSpeed || 'medium';
    this.useMonospace = el.dataset.useMonospace === 'true';
    this.textEl = el.querySelector('.aquatix-hero-video__greeting');
    this.ctaWrap = el.querySelector('.aquatix-hero-video__cta-wrap');
    this.announceEl = el.querySelector('.aquatix-hero-video__announce');

    const dataEl = el.querySelector('.aquatix-hero-video__greetings-data');
    if (!dataEl || !this.textEl || !this.ctaWrap) return;

    try {
      this.greetings = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    this._run();
  }

  AquatixHeroVideoSection.prototype._run = async function () {
    const visitCount = bumpVisitCount();
    const index = pickGreetingIndex(visitCount);
    const greeting = this.greetings[index];
    if (!greeting || !greeting.message) return;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reducedMotion) {
      if (this.animationStyle === 'terminal' && this.useMonospace) {
        this.textEl.classList.add('aquatix-hero-video__greeting--monospace');
      }
      this.textEl.textContent = greeting.message;
      if (this.announceEl) this.announceEl.textContent = greeting.message;
      revealCta(this.ctaWrap, greeting);
      return;
    }

    await typewrite(
      this.textEl,
      greeting.message,
      this.typingSpeed,
      this.animationStyle,
      this.useMonospace
    );

    if (this.announceEl) this.announceEl.textContent = greeting.message;

    revealCta(this.ctaWrap, greeting);
  };

  // ── Init ────────────────────────────────────────────────────────────────────

  function init() {
    document
      .querySelectorAll('.aquatix-hero-video[data-section-id]')
      .forEach(function (el) {
        new AquatixHeroVideoSection(el);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
